import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

export const RICH_TEXT_MAX_LENGTH = 3500;

// The toolbar is limited to what the API whitelist accepts (p, br, strong, em, ul, ol, li). Alignment,
// colour, font and links would be stripped on save and the author would lose the formatting without
// being told why. Font choice is also deliberately absent: the widget picks its own font, so a font set
// here would clash with the rest of the voucher.
const toolbar = {
    options: ['inline', 'list'],
    inline: { inDropdown: false, options: ['bold', 'italic'] },
    list: { inDropdown: false, options: ['unordered'] }
};

const escapeHtml = text => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Stored values written before this editor existed are plain text with newlines. Wrapping each line in a
// paragraph makes them open reading the way they read today, instead of collapsing into a single block.
const plainTextToHtml = text => text
    .split(/\r?\n/)
    .map(line => `<p>${escapeHtml(line)}</p>`)
    .join('');

const looksLikeHtml = value => value.indexOf('<') >= 0;

function editorStateFromValue(value) {
    if (!value) return EditorState.createEmpty();

    const blocks = htmlToDraft(looksLikeHtml(value) ? value : plainTextToHtml(value));
    if (!blocks || !blocks.contentBlocks) return EditorState.createEmpty();

    return EditorState.createWithContent(ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap));
}

function toHtml(contentState) {
    if (!contentState.hasText()) return '';

    // draftToHtml puts a newline between blocks. It renders as nothing, but both this editor's counter
    // and the server-side one count it, so it would quietly eat into the author's character budget.
    // Line breaks the author actually types come through as <br>, not as newlines, so dropping these is
    // safe.
    return draftToHtml(convertToRaw(contentState)).replace(/\n/g, '');
}

/**
 * Read-only view of a stored description.
 *
 * Rich text is markup the API has already sanitized against a small whitelist; plain text written
 * before rich text existed keeps rendering as text, so it looks exactly as it always did.
 */
export function RichTextView({ value }) {
    if (!value) return null;

    if (looksLikeHtml(value)) {
        return <div className="rich-text" dangerouslySetInnerHTML={{ __html: value }} />;
    }

    return <div style={{ whiteSpace: 'break-spaces' }}>{value}</div>;
}

/**
 * Prepares a stored description for display. Rich text is already markup and goes through untouched;
 * a legacy plain text value keeps the newline-per-list-item shape these screens have always given it,
 * so old descriptions look exactly as they did before.
 */
export function richTextToHtml(value) {
    if (!value) return '';

    return looksLikeHtml(value)
        ? value
        : `<li>${value.replace(/\n/g, '</li><li>')}</li>`;
}

// Mirrors the server-side count (VoucherAPI HtmlContentSanitizer.VisibleLength): tags dropped, entities
// counted as the single character they stand for. The two must agree, or the editor lets through what the
// API then rejects.
export function visibleLength(html) {
    if (!html) return 0;

    const el = document.createElement('div');
    el.innerHTML = String(html).replace(/<[^>]+>/g, '');
    return (el.textContent || '').length;
}

/**
 * Rich text field limited to the formatting the voucher templates and widgets can render.
 *
 * Reports changes as `{ target: { name, value } }` so it can stand in for the `<Input>` it replaces
 * without any of the screens using it having to change.
 */
export default function EditorText({ value, onChange, name = 'description', placeholder, required, disabled, maxLength = RICH_TEXT_MAX_LENGTH }) {
    const [editorState, setEditorState] = useState(() => editorStateFromValue(value));

    // What this editor last handed out, and the content it was built from. Both are needed to tell an
    // echo of our own change apart from a value that genuinely came from elsewhere.
    const emitted = useRef(value);
    const content = useRef(editorState.getCurrentContent());

    useEffect(() => {
        // Re-seed only when the value came from outside — an async load, a language switch, a reset.
        // Re-seeding on our own echo would move the caret while the author is typing.
        if (value === emitted.current) return;

        const next = editorStateFromValue(value);
        emitted.current = value;
        content.current = next.getCurrentContent();
        setEditorState(next);
    }, [value]);

    const currentHtml = useMemo(() => toHtml(editorState.getCurrentContent()), [editorState]);
    const used = visibleLength(currentHtml);
    const plainText = editorState.getCurrentContent().getPlainText();

    const handleChange = next => {
        setEditorState(next);

        const nextContent = next.getCurrentContent();
        // draft-js reports selection moves through this same callback. Only a content change is worth
        // saving — without this guard, merely opening a screen would rewrite every record.
        if (nextContent === content.current) return;

        content.current = nextContent;
        const html = toHtml(nextContent);
        emitted.current = html;

        onChange({ target: { name, value: html } });
    };

    // The field it replaces had a native maxLength, so typing and pasting past the limit stayed
    // impossible. Keep that — reusing the already-computed `used` rather than re-serializing per keystroke.
    const handleBeforeInput = () => (used >= maxLength ? 'handled' : 'not-handled');

    const handlePastedText = text => {
        const room = maxLength - used;
        return text && text.length > room ? 'handled' : 'not-handled';
    };

    return (
        // Border matches the plain inputs next to it (Bootstrap .form-control): 1px #ced4da, rounded.
        <div className="coolTextEditor" style={{ border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
            <Editor
                editorState={editorState}
                onEditorStateChange={handleChange}
                toolbar={toolbar}
                toolbarHidden={disabled}
                readOnly={disabled}
                stripPastedStyles={true}
                handleBeforeInput={handleBeforeInput}
                handlePastedText={handlePastedText}
                placeholder={placeholder}
                editorStyle={{ minHeight: '8rem', padding: '0 1rem' }}
            />
            <div className="d-flex justify-content-end px-3 pb-2">
                <span className={`font_size_xxs ${used > maxLength ? 'text-danger' : 'text-muted'}`}>
                    {used}/{maxLength}
                </span>
            </div>
            {required ?
                // The <Input> this replaces carried `required`, and the screens still call
                // reportValidity() on the form. draft-js has no form value of its own, so mirror the
                // plain text into a hidden input to keep that validation working.
                <input
                    autoComplete="off"
                    style={{ opacity: 0, width: '95%', height: 0, position: 'absolute' }}
                    required
                    value={plainText}
                    onChange={() => { }}
                />
                : ''}
        </div>
    );
}
